// "Benachrichtigungen"-Gruppe der Settings-Navigation: Kanäle, Worüber.
// Ausgelagert aus routes/Settings.tsx (Redesign: 15 Abschnitte in Tabs
// gruppiert) nur um die Hauptdatei unter der 500-Zeilen-Grenze zu halten —
// wortgleiche Verschiebung, Logik bleibt in Settings.tsx.

import type { ComponentChildren } from "preact";
import { Laptop, Mail, MessageSquare, Smartphone } from "lucide-preact";
import type { UserPreferences } from "@juuplan/shared";
import { Toggle } from "./SettingsControls";

export interface NotificationsGroupProps {
  prefs: UserPreferences | null;
  saving: boolean;
  onUpdate: (patch: Partial<UserPreferences>) => void;
  loadingHint: ComponentChildren;
}

export function NotificationsGroup(props: NotificationsGroupProps): preact.JSX.Element {
  const { prefs } = props;
  return (
    <div class="flex flex-col gap-8">
      {!prefs ? (
        props.loadingHint
      ) : (
        <>
          <section>
            <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
              Kanäle
            </h2>
            <div class="flex flex-col gap-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] divide-y divide-[color:var(--color-outline-variant)]">
              <Toggle
                icon={<Mail size={20} aria-hidden="true" />}
                label="Email"
                description={`Schickt Mails an deine Microsoft-Adresse via bot@juunit.com.`}
                checked={prefs.channel_email}
                onChange={(v) => props.onUpdate({ channel_email: v })}
                disabled={props.saving}
              />
              <Toggle
                icon={<MessageSquare size={20} aria-hidden="true" />}
                label="Microsoft Teams"
                description="Persönliche Activity-Notification in Teams. Voraussetzung: juuplan-App muss in Teams installiert sein (Story 5b)."
                checked={prefs.channel_teams}
                onChange={(v) => props.onUpdate({ channel_teams: v })}
                disabled={props.saving}
                badge="Beta"
              />
              <Toggle
                icon={<Laptop size={20} aria-hidden="true" />}
                label="Mac-App"
                description="Mitteilungen der juuplan-App auf dem Mac. Ausschalten, wenn dich Teams am Schreibtisch schon benachrichtigt."
                checked={prefs.channel_push_mac}
                onChange={(v) => props.onUpdate({ channel_push_mac: v })}
                disabled={props.saving}
              />
              <Toggle
                icon={<Smartphone size={20} aria-hidden="true" />}
                label="iPhone-App"
                description="Push-Mitteilungen auf dem iPhone. Worüber genau, stellst du in der iPhone-App ein."
                checked={prefs.channel_push}
                onChange={(v) => props.onUpdate({ channel_push: v })}
                disabled={props.saving}
              />
            </div>
          </section>

          <section>
            <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
              Worüber
            </h2>
            <div class="flex flex-col gap-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] divide-y divide-[color:var(--color-outline-variant)]">
              <Toggle
                label="@-Erwähnungen"
                description="Wenn jemand dich in einem Update einer Aufgabe erwähnt."
                checked={prefs.notify_mentions}
                onChange={(v) => props.onUpdate({ notify_mentions: v })}
                disabled={props.saving}
              />
              <Toggle
                label="Zuweisungen"
                description="Wenn dich jemand einer Aufgabe zuweist."
                checked={prefs.notify_assignments}
                onChange={(v) => props.onUpdate({ notify_assignments: v })}
                disabled={props.saving}
              />
              <Toggle
                label="Eigene Aktionen benachrichtigen mich"
                description="Wenn du dich selbst erwähnst oder dir selbst eine Aufgabe zuweist."
                checked={prefs.notify_self_pings}
                onChange={(v) => props.onUpdate({ notify_self_pings: v })}
                disabled={props.saving}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
